import ExcelJS from 'exceljs'

// ============ 常量配置 ============
const CONFIG = {
  IMAGE_MAX_WIDTH: 100,
  IMAGE_MAX_HEIGHT: 70,
  IMAGE_QUALITY: 0.7, // 降低质量减少内存
  DOWNLOAD_CONCURRENCY: 4, // 并发数
  DATA_CHUNK_SIZE: 50, // 增大块大小
  DEFAULT_PAGE_SIZE: 100, // 每页数据量
  EXCEL_ROW_HEIGHT: 80,
  EXCEL_COL_WIDTH_IMAGE: 20,
  EXCEL_COL_WIDTH_DEFAULT: 15,
  IMAGE_TIMEOUT: 80000, // 超时时间
  MAX_RETRY: 2, // 失败重试次数
  MEMORY_CLEANUP_INTERVAL: 200, // 每处理多少行清理一次内存
  MAX_ROWS_PER_FILE: 5000, // 每个 Excel 文件最大行数（用于分批导出）
}

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp']

// ============ 工具函数 ============

/**
 * 延迟函数
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms))

/**
 * 强制垃圾回收提示（仅在可能时）
 */
function suggestGC() {
  if (global.gc) {
    global.gc()
  }
}

/**
 * 判断是否为图片 URL
 */
function isImageUrl(value) {
  if (!value || typeof value !== 'string') return false
  
  const lowerValue = value.toLowerCase()
  const hasProtocol = lowerValue.startsWith('http://') || lowerValue.startsWith('https://')
  const hasImageExtension = IMAGE_EXTENSIONS.some(ext => lowerValue.includes(ext)) || 
                           lowerValue.includes('downloadplay')
  
  return hasProtocol && hasImageExtension
}

function getFinalUrl(url){
  return (url||"").replace("https://wj.survey.work",window.origin)
}

/**
 * 计算缩放后的图片尺寸
 */
function calculateImageSize(width, height) {
  const { IMAGE_MAX_WIDTH, IMAGE_MAX_HEIGHT } = CONFIG
  
  if (width <= IMAGE_MAX_WIDTH && height <= IMAGE_MAX_HEIGHT) {
    return { width, height }
  }
  
  const aspectRatio = width / height
  const widthRatio = width / IMAGE_MAX_WIDTH
  const heightRatio = height / IMAGE_MAX_HEIGHT
  
  if (widthRatio > heightRatio) {
    return {
      width: IMAGE_MAX_WIDTH,
      height: Math.round(IMAGE_MAX_WIDTH / aspectRatio)
    }
  } else {
    return {
      width: Math.round(IMAGE_MAX_HEIGHT * aspectRatio),
      height: IMAGE_MAX_HEIGHT
    }
  }
}

/**
 * 下载图片并转换为 base64（带重试和压缩）
 */
async function downloadImageAsBase64(url, retryCount = 0) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'Anonymous'

    const timeoutId = setTimeout(() => {
      img.src = '' // 停止加载
      reject(new Error(`超时`))
    }, CONFIG.IMAGE_TIMEOUT)

    img.onload = () => {
      clearTimeout(timeoutId)

      try {
        const { width: originalWidth, height: originalHeight } = img

        // 计算压缩后的尺寸
        const { width: targetWidth, height: targetHeight } = calculateImageSize(originalWidth, originalHeight)

        const canvas = document.createElement('canvas')
        canvas.width = targetWidth
        canvas.height = targetHeight

        const ctx = canvas.getContext('2d', { alpha: false })
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight)

        const base64 = canvas.toDataURL('image/jpeg', CONFIG.IMAGE_QUALITY)

        // 立即清理
        canvas.width = 0
        canvas.height = 0
        img.src = ''

        resolve({ base64, width: targetWidth, height: targetHeight })
      } catch (error) {
        reject(new Error(`处理失败`))
      }
    }
    
    img.onerror = () => {
      clearTimeout(timeoutId)
      img.src = ''
      reject(new Error(`加载失败`))
    }
    
    img.src = getFinalUrl(url)
  }).catch(async (error) => {
    // 重试逻辑
    if (retryCount < CONFIG.MAX_RETRY) {
      await delay(500 * (retryCount + 1))
      return downloadImageAsBase64(url, retryCount + 1)
    }
    throw error
  })
}

/**
 * 批量下载图片（流式处理，边下载边使用）
 * @param {string[]} urls - 图片 URL 列表
 * @param {number} concurrency - 并发数
 * @param {Function} onImageProgress - 图片下载进度回调
 */
async function batchDownloadImages(urls, concurrency = CONFIG.DOWNLOAD_CONCURRENCY, onImageProgress) {
  const uniqueUrls = [...new Set(urls)]
  const results = new Map()
  let completed = 0
  const total = uniqueUrls.length

  if (total === 0) return results

  // 预热：先下载第一张图片，建立连接
  const firstUrl = uniqueUrls[0]
  try {
    const imageData = await downloadImageAsBase64(firstUrl)
    results.set(getFinalUrl(firstUrl), imageData)
    completed++
    if (onImageProgress) {
      onImageProgress({ completed, total, progress: Math.round((completed / total) * 100) })
    }
  } catch (error) {
    console.warn(`图片下载失败(预热): ${firstUrl}`, error.message)
    completed++
  }

  // 剩余的图片并发下载
  const remainingUrls = uniqueUrls.slice(1)
  if (remainingUrls.length === 0) return results

  const processQueue = async (queue) => {
    while (queue.length > 0) {
      const url = queue.shift()

      try {
        const imageData = await downloadImageAsBase64(url)
        results.set(getFinalUrl(url), imageData)
      } catch (error) {
        // 下载失败，打印日志便于调试
        console.warn(`图片下载失败: ${url}`, error.message)
      }

      completed++

      // 回调图片下载进度
      if (onImageProgress) {
        onImageProgress({
          completed,
          total,
          progress: Math.round((completed / total) * 100)
        })
      }

      // 每完成100张图片打印一次进度
      if (completed % 100 === 0 || completed === total) {
        console.log(`图片下载: ${completed}/${total}`)
      }
    }
  }

  // 分成多个队列并发处理
  const queues = Array.from({ length: concurrency }, () => [])
  remainingUrls.forEach((url, index) => {
    queues[index % concurrency].push(url)
  })

  await Promise.all(queues.map(queue => processQueue(queue)))

  return results
}

// ============ Excel 生成函数 ============

/**
 * 设置表头样式
 */
function styleHeaderRow(headerRow) {
  headerRow.font = { bold: true }
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' }
  }
  headerRow.alignment = { vertical: 'middle', horizontal: 'center' }
}

/**
 * 配置列宽
 */
function configureColumns(worksheet, columns) {
  worksheet.columns = columns.map((col) => {
    const isImageColumn = col.includes('【图片】') || col.includes('【文件上传题】')
    return { 
      width: isImageColumn ? CONFIG.EXCEL_COL_WIDTH_IMAGE : CONFIG.EXCEL_COL_WIDTH_DEFAULT 
    }
  })
}

/**
 * 添加图片到单元格
 */
function addImageToCell(workbook, worksheet, rowIndex, colIndex, base64, width, height) {
  const imageId = workbook.addImage({
    base64: base64,
    extension: 'jpeg'
  })
  
  worksheet.addImage(imageId, {
    tl: { col: colIndex + 0.1, row: rowIndex - 1 + 0.1 },
    ext: { width, height }
  })
  
  worksheet.getCell(rowIndex, colIndex + 1).value = ''
}

/**
 * 流式处理数据（边下载图片边生成 Excel）
 * @param {Function} api - API 请求函数
 * @param {Object} params - 请求参数
 * @param {Function} onProgress - 进度回调 { phase, downloaded, total, progress, imageProgress }
 */
async function streamProcessData(api, params, onProgress) {
  const size = CONFIG.DEFAULT_PAGE_SIZE
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('数据')

  let current = 1
  let totalProcessed = 0
  let total = 0
  let columns = []

  // 图片缓存（使用 LRU 策略）
  const imageCache = new Map()
  const maxCacheSize = 500

  console.log('开始流式处理数据...')

  while (true) {
    // 1. 获取当前页数据
    const response = await api({ ...params, current, size })
    const { columns: cols, data, total: totalCount } = response.data.data

    if (current === 1) {
      columns = cols
      total = totalCount

      // 初始化 Excel 表头
      worksheet.addRow(columns)
      styleHeaderRow(worksheet.getRow(1))
      configureColumns(worksheet, columns)
    }

    if (data.length === 0) break

    // 2. 提取当前页的图片 URL
    const pageImageUrls = []
    data.forEach(row => {
      row.forEach(cell => {
        if (isImageUrl(cell) && !imageCache.has(getFinalUrl(cell))) {
          pageImageUrls.push(cell)
        }
      })
    })

    // 3. 下载当前页的图片
    if (pageImageUrls.length > 0) {
      const pageImages = await batchDownloadImages(
        pageImageUrls,
        CONFIG.DOWNLOAD_CONCURRENCY,
        (imageProgress) => {
          // 回调图片下载进度
          if (onProgress) {
            onProgress({
              phase: 'image',
              downloaded: totalProcessed,
              total,
              progress: Math.min(100, Math.round((totalProcessed / total) * 100)),
              imageProgress: {
                completed: imageProgress.completed,
                total: imageProgress.total,
                progress: imageProgress.progress,
                currentPage: current
              }
            })
          }
        }
      )

      // 合并到缓存，保持缓存大小
      for (const [url, imgData] of pageImages) {
        if (imageCache.size >= maxCacheSize) {
          const firstKey = imageCache.keys().next().value
          imageCache.delete(firstKey)
        }
        imageCache.set(url, imgData)
      }
    }

    // 4. 将数据写入 Excel
    data.forEach((rowData, index) => {
      const excelRowIndex = totalProcessed + index + 2
      worksheet.addRow(rowData)

      let hasImage = false

      rowData.forEach((cellValue, colIndex) => {
        const finalUrl = getFinalUrl(cellValue)
        if (isImageUrl(cellValue) && imageCache.has(finalUrl)) {
          hasImage = true
          const { base64, width, height } = imageCache.get(finalUrl)
          const calculate = calculateImageSize(width,height)
          addImageToCell(workbook, worksheet, excelRowIndex, colIndex, base64, calculate.width, calculate.height)
        }
      })

      if (hasImage) {
        worksheet.getRow(excelRowIndex).height = CONFIG.EXCEL_ROW_HEIGHT
      }
    })

    totalProcessed += data.length

    // 5. 更新进度
    const progress = Math.min(100, Math.round((totalProcessed / total) * 100))
    if (onProgress) {
      onProgress({
        phase: 'data',
        downloaded: totalProcessed,
        total,
        progress,
        imageProgress: null
      })
    }
    console.log(`数据处理: ${totalProcessed}/${total} (${progress}%)`)

    // 6. 定期清理内存
    if (totalProcessed % CONFIG.MEMORY_CLEANUP_INTERVAL === 0) {
      suggestGC()
    }

    if (totalProcessed >= total) break

    current++
  }

  console.log('数据处理完成，生成 Excel 文件...')

  return { workbook, total: totalProcessed }
}

/**
 * 触发文件下载
 */
function triggerDownload(buffer, fileName) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })

  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  
  link.href = url
  link.download = `${fileName}.xlsx`
  document.body.appendChild(link)
  link.click()
  
  // 清理
  setTimeout(() => {
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, 100)
}

// ============ 主导出函数 ============

/**
 * 浏览器端导出带图片的 Excel（优化大数据量场景）
 * @param {Function} api - API 请求函数
 * @param {Object} params - 请求参数
 * @param {string} fileName - 导出文件名
 * @param {Function} onProgress - 进度回调
 *   回调参数格式:
 *   {
 *     phase: 'data' | 'image' | 'generating' | 'complete',
 *     downloaded: number,      // 已处理数据条数
 *     total: number,           // 总数据条数
 *     progress: number,        // 数据处理进度 0-100
 *     imageProgress: {         // 图片下载进度（仅 phase='image' 时有值）
 *       completed: number,     // 当前页已下载图片数
 *       total: number,         // 当前页图片总数
 *       progress: number,      // 当前页图片进度 0-100
 *       currentPage: number    // 当前页码
 *     } | null
 *   }
 * @returns {Promise<{total: number}>}
 */
export const downloadWithBrowser = async (
  api,
  params,
  fileName = '导出数据',
  onProgress
) => {
  const startTime = Date.now()
  
  try {
    // 流式处理：边获取数据边下载图片边生成 Excel
    const { workbook, total } = await streamProcessData(api, params, onProgress)

    // 生成并下载文件
    console.log('开始生成 Excel 文件...')
    if (onProgress) {
      onProgress({
        phase: 'generating',
        downloaded: total,
        total,
        progress: 100,
        imageProgress: null
      })
    }
    const buffer = await workbook.xlsx.writeBuffer()

    console.log('开始下载文件...')
    triggerDownload(buffer, fileName)

    const duration = ((Date.now() - startTime) / 1000).toFixed(2)
    console.log(`✅ 导出完成！共 ${total} 条数据，耗时 ${duration} 秒`)

    if (onProgress) {
      onProgress({
        phase: 'complete',
        downloaded: total,
        total,
        progress: 100,
        imageProgress: null
      })
    }

    return { total }
  } catch (error) {
    console.error('❌ 导出失败:', error)
    throw new Error(`Excel 导出失败: ${error.message}`)
  }
}

/**
 * 预估内存使用情况
 */
export function estimateMemoryUsage(rowCount, avgImagesPerRow = 1) {
  const bytesPerRow = 1024 // 约 1KB
  const bytesPerImage = 10 * 1024 // 约 10KB（压缩后）
  const totalMB = ((rowCount * bytesPerRow + rowCount * avgImagesPerRow * bytesPerImage) / (1024 * 1024)).toFixed(2)

  console.log(`预估内存使用: ${totalMB} MB (${rowCount} 行数据)`)
  return totalMB
}

/**
 * 分批导出大数据量 Excel（适用于10万+图片场景）
 * 将数据分成多个 Excel 文件，每个文件最多 MAX_ROWS_PER_FILE 行
 * @param {Function} api - API 请求函数
 * @param {Object} params - 请求参数
 * @param {string} fileName - 导出文件名前缀
 * @param {Function} onProgress - 进度回调
 * @param {number} current - 起始页码（默认为 1）
 * @param {number} size - 每页数据量（默认为 CONFIG.DEFAULT_PAGE_SIZE）
 * @returns {Promise<{total: number, fileCount: number}>}
 */
export const downloadWithBrowserBatch = async (
  api,
  params,
  fileName = '导出数据',
  onProgress,
  current = 1,
  size = CONFIG.DEFAULT_PAGE_SIZE
) => {
  const startTime = Date.now()

  // 先获取总数和表头
  const firstResponse = await api({ ...params, current: 1, size: 1 })
  const { columns, total: totalCount } = firstResponse.data.data

  if (totalCount === 0) {
    throw new Error('没有数据可导出')
  }

  // 计算总页数和需要导出的页数
  const totalPages = Math.ceil(totalCount / size)
  const pagesToExport = Math.max(0, totalPages - current + 1)

  if (pagesToExport === 0 || current > totalPages) {
    throw new Error('从当前页开始没有数据可导出')
  }

  console.log(`总数据量: ${totalCount}，总页数: ${totalPages}，从第 ${current} 页开始，导出 ${pagesToExport} 页`)

  let globalProcessed = 0

  // 每页生成一个文件
  for (let pageIndex = 0; pageIndex < pagesToExport; pageIndex++) {
    const currentPage = current + pageIndex

    console.log(`\n========== 开始生成第 ${pageIndex + 1}/${pagesToExport} 个文件 (第 ${currentPage} 页) ==========`)

    // 获取当前页数据
    const response = await api({ ...params, current: currentPage, size })
    const responseData = response.data.data

    if (!responseData || !responseData.data || responseData.data.length === 0) {
      console.log(`第 ${currentPage} 页没有数据，跳过`)
      continue
    }

    const { data } = responseData
    const rowCount = data.length

    // 创建新的 workbook
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('数据')

    // 初始化表头
    worksheet.addRow(columns)
    styleHeaderRow(worksheet.getRow(1))
    configureColumns(worksheet, columns)

    // 图片缓存
    const imageCache = new Map()

    // 提取图片 URL
    const pageImageUrls = []
    data.forEach(row => {
      row.forEach(cell => {
        if (isImageUrl(cell) && !imageCache.has(getFinalUrl(cell))) {
          pageImageUrls.push(cell)
        }
      })
    })

    // 下载图片
    if (pageImageUrls.length > 0) {
      const pageImages = await batchDownloadImages(
        pageImageUrls,
        CONFIG.DOWNLOAD_CONCURRENCY,
        (imageProgress) => {
          if (onProgress) {
            onProgress({
              phase: 'image',
              downloaded: globalProcessed,
              total: totalCount,
              progress: Math.round((globalProcessed / totalCount) * 100),
              currentFile: pageIndex + 1,
              totalFiles: pagesToExport,
              currentPage,
              imageProgress: {
                completed: imageProgress.completed,
                total: imageProgress.total,
                progress: imageProgress.progress
              }
            })
          }
        }
      )

      for (const [url, imgData] of pageImages) {
        imageCache.set(url, imgData)
      }
    }

    // 写入 Excel
    data.forEach((rowData, index) => {
      const excelRowIndex = index + 2
      worksheet.addRow(rowData)

      let hasImage = false
      rowData.forEach((cellValue, colIndex) => {
        const finalUrl = getFinalUrl(cellValue)
        if (isImageUrl(cellValue) && imageCache.has(finalUrl)) {
          hasImage = true
          const { base64, width, height } = imageCache.get(finalUrl)
          addImageToCell(workbook, worksheet, excelRowIndex, colIndex, base64, width, height)
        }
      })

      if (hasImage) {
        worksheet.getRow(excelRowIndex).height = CONFIG.EXCEL_ROW_HEIGHT
      }
    })

    globalProcessed += rowCount

    // 更新进度
    if (onProgress) {
      onProgress({
        phase: 'generating',
        downloaded: globalProcessed,
        total: totalCount,
        progress: Math.round((globalProcessed / totalCount) * 100),
        currentFile: pageIndex + 1,
        totalFiles: pagesToExport,
        currentPage,
        imageProgress: null
      })
    }

    // 生成并下载当前文件
    console.log(`生成第 ${pageIndex + 1} 个 Excel 文件 (第 ${currentPage} 页，${rowCount} 条数据)...`)
    const buffer = await workbook.xlsx.writeBuffer()
    const fileNameWithIndex = pagesToExport > 1 ? `${fileName}_第${currentPage}页` : fileName
    triggerDownload(buffer, fileNameWithIndex)

    // 清理当前文件的资源
    imageCache.clear()
    suggestGC()

    // 文件之间稍作延迟，让浏览器有时间处理
    if (pageIndex < pagesToExport - 1) {
      await delay(1000)
    }
  }

  const duration = ((Date.now() - startTime) / 1000).toFixed(2)
  console.log(`\n✅ 全部导出完成！共 ${globalProcessed} 条数据，${pagesToExport} 个文件，耗时 ${duration} 秒`)

  if (onProgress) {
    onProgress({
      phase: 'complete',
      downloaded: globalProcessed,
      total: totalCount,
      progress: 100,
      currentFile: pagesToExport,
      totalFiles: pagesToExport,
      imageProgress: null
    })
  }

  return { total: globalProcessed, fileCount: pagesToExport }
}